import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RyanHeader from '../components/RyanHeader';
import Statistics from '../../components/dashboard/Statistics';
import { DashboardStats, WeekHistory } from '../../components/dashboard/types';
import './RyanStatsPage.css';

interface RyanStats {
  totals: {
    totalCards: number;
    masteredCards: number;
    learningCards: number;
    newCards: number;
    masteryPercentage: number;
  };
  today: { cardsStudiedToday: number; accuracyToday: number };
  week: { cardsStudiedWeek: number };
  streak: { current: number; longest: number };
  due: { dueNow: number; dueToday: number };
}

function mapToDashboardStats(r: RyanStats): DashboardStats {
  return {
    today: {
      cardsStudied: r.today.cardsStudiedToday,
      cardsMastered: 0,
      timeSpent: 0,
      timeSpentFormatted: '0min',
      sessions: 0,
      accuracy: r.today.accuracyToday,
      isProductiveDay: r.today.cardsStudiedToday > 0,
      isPersonalRecord: false,
    },
    comparison: { cardsVsYesterday: 0, timeVsYesterday: 0 },
    week: {
      totalCards: r.week.cardsStudiedWeek,
      totalTime: 0,
      totalTimeFormatted: '0h',
      daysStudied: 0,
      productiveDays: 0,
      avgCardsPerDay: Math.round(r.week.cardsStudiedWeek / 7),
      bestDay: null,
    },
    month: {
      totalCards: 0,
      totalTime: 0,
      totalTimeFormatted: '0h',
      daysStudied: 0,
      productiveDays: 0,
      avgCardsPerDay: 0,
    },
    allTime: {
      totalCardsStudied: r.totals.totalCards,
      cardsMastered: r.totals.masteredCards,
      masteryPercentage: r.totals.masteryPercentage,
      daysStudied: 0,
      currentStreak: r.streak.current,
      longestStreak: r.streak.longest,
      totalTime: 0,
      totalTimeFormatted: '0h',
      totalSessions: 0,
      productiveDays: 0,
    },
    motivationalMessages: [],
  };
}

const RyanStatsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = (location.state as any)?.returnPath || '/ryan';

  const [dashStats, setDashStats] = useState<DashboardStats | null>(null);
  const [weekHistory, setWeekHistory] = useState<WeekHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, weekRes] = await Promise.all([
        fetch('/api/ryan/stats', { headers }),
        fetch('/api/ryan/stats/week-history', { headers }),
      ]);
      if (!statsRes.ok) throw new Error('Erro ao carregar estatísticas');
      const ryanStats: RyanStats = await statsRes.json();
      setDashStats(mapToDashboardStats(ryanStats));
      if (weekRes.ok) {
        const weekData = await weekRes.json();
        setWeekHistory(weekData.history || []);
      }
    } catch (e: any) {
      setError(e.message || 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="ryan-stats-page">
      <RyanHeader
        showLogout={false}
        title="Estatísticas"
        onBack={() => navigate(returnPath)}
        logoInCenter={true}
        className="ryan-stats-page-header"
      />
      <div className="ryan-stats-content">
        {loading && (
          <div className="ryan-stats-spinner-wrap">
            <div className="ryan-stats-spinner" />
          </div>
        )}
        {!loading && error && (
          <div className="ryan-stats-error">
            <p style={{ color: '#dc2626', marginBottom: 12 }}>{error}</p>
            <button className="ryan-stats-retry-btn" onClick={load}>
              Tentar novamente
            </button>
          </div>
        )}
        {!loading && !error && (
          <Statistics
            stats={dashStats}
            weekHistory={weekHistory}
            onReload={load}
            hideTimeCard={true}
          />
        )}
      </div>
    </div>
  );
};

export default RyanStatsPage;
